/* eslint-disable no-param-reassign */
import axios from 'axios';
import NodeRSA from 'node-rsa';
import { PUBLIC_KEY_HEADER } from '@/utils/api';

const key = new NodeRSA(PUBLIC_KEY_HEADER);
const SENSITIVE_KEY = ['userId', 'role', 'subPassword', 'password'] as const;

// ---- Keys used in the old VueSession implementation ----
const SESSION_KEYS = {
  auth: 'ambgames-user-authenticate',
  balance: 'ambgames-user-balance',
  refresh: 'ambgames-user-refresh-token',
  access: 'ambgames-user-access-token'
} as const;

// Main key that VueSession used to store everything in sessionStorage
const VUE_SESSION_KEY = 'vue-session-key';

type SessionBag = Record<string, any>;

// Read entire session bag from sessionStorage
function readSessionBag(): SessionBag {
  const raw = sessionStorage.getItem(VUE_SESSION_KEY);
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

// Write entire session bag back to sessionStorage
function writeSessionBag(bag: SessionBag) {
  if (!Object.keys(bag).length) {
    // If bag is empty, remove the main key
    sessionStorage.removeItem(VUE_SESSION_KEY);
  } else {
    sessionStorage.setItem(VUE_SESSION_KEY, JSON.stringify(bag));
  }
}

// Get a single key from the session bag
function sessionGet<T = any>(key: string): T | null {
  const bag = readSessionBag();
  if (!(key in bag)) return null;
  return bag[key] as T;
}

// Set a single key inside the session bag
function sessionSet(key: string, value: any) {
  const bag = readSessionBag();
  bag[key] = value;
  writeSessionBag(bag);
}

// Remove a single key from the session bag
function sessionRemove(key: string) {
  const bag = readSessionBag();
  if (key in bag) {
    delete bag[key];
    writeSessionBag(bag);
  }
}

// Default user object when no session exists
function defaultUserPersistent() {
  return {
    user: {
      _id: null,
      username: null,
      type: null,
      nickname: null,
      source: null,
      level: null,
      wallet: null,
      commission: null,
      ipWhitelist: null,
      isFeature: null,
      isMechanic: null,
      showCurrencySigns: null
    },
    token: ''
  };
}

// ---- basic formatters (replaces old Vue.filter usage) ----
function dateTimeFormatter(unix: number | string | Date) {
  const t = new Date(unix);
  const zeroPad = (value: number | string, size: number): string =>
    String(value).length < size ? zeroPad(`0${String(value)}`, size) : String(value);

  return `${zeroPad(t.getFullYear(), 2)}-${zeroPad(
    t.getMonth() + 1,
    2
  )}-${zeroPad(t.getDate(), 2)} ${zeroPad(t.getHours(), 2)}:${zeroPad(
    t.getMinutes(),
    2
  )}:${zeroPad(t.getSeconds(), 2)}`;
}

function commaFormatter(n: number | string) {
  return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function upperCamel(text: string) {
  return text.replace(/(?:^\w|[A-Z]|\b\w|\s+)/g, (match, index) => {
    if (+match === 0) return '';
    return index === 0 ? match.toUpperCase() : match.toLowerCase();
  });
}

// ---------- main utils ----------
const utils = {
  dateTimeFormatter,
  commaFormatter,
  upperCamel,

  /**
   * Get user persistent data from session (compatible with old VueSession logic).
   * - If no data exists, return default user object with empty token.
   * - If data is stored as JSON string (old behavior), parse it.
   */
  getUserPersistent() {
    const saved = sessionGet(SESSION_KEYS.auth);

    if (!saved) {
      return defaultUserPersistent();
    }

    // Old Vue2 code used JSON.parse on the stored value,
    // so support both string and object formats.
    if (typeof saved === 'string') {
      try {
        return JSON.parse(saved);
      } catch {
        return defaultUserPersistent();
      }
    }

    return saved;
  },

  /**
   * Clear user, tokens and Authorization header.
   * This replaces Vue.prototype.$session.destroy + delete Vue.axios.defaults.headers.common.Authorization
   */
  clearUserPersistent() {
    sessionRemove(SESSION_KEYS.refresh);
    sessionRemove(SESSION_KEYS.access);
    sessionRemove(SESSION_KEYS.auth);

    // Remove Authorization header from axios default config
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete (axios.defaults.headers as any)?.common?.Authorization;
  },

  /**
   * Get user balance from session.
   * Old behavior:
   *  - If no session or no balance -> return 0
   */
  getUserBalance() {
    const bal = sessionGet<number>(SESSION_KEYS.balance);
    if (!bal) return 0;
    return bal;
  },

  currencyFormatter(balance: number) {
    const x = balance;
    if (x === 0) {
      return '0.00';
    }
    if (!x) {
      return x as any;
    }
    return x.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
  },

  balanceFormatter(balance: number) {
    if (balance === 0) {
      return '0.00';
    }
    if (balance === null || balance === undefined) {
      return balance as any;
    }

    const splitBalance = balance.toString().split('.');
    const lengthBalance = splitBalance[1] ? splitBalance[1].length : 0;

    let formattedBalance: string;
    if (lengthBalance >= 3) {
      formattedBalance = balance.toFixed(3);
    } else if (lengthBalance === 1) {
      formattedBalance = balance.toFixed(2);
    } else {
      formattedBalance = balance.toFixed(2);
    }

    return formattedBalance.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  },

  colorCurrency(value: number) {
    if (value < 0) return 'text-red-500';
    return '';
  },

  colorCurrencyLog(value: number) {
    if (value > 0) return 'text-primary';
    if (value < 0) return 'text-red-500';
    return '';
  },

  colorCurrencyFoot(val: number) {
    if (val < 0) return 'red-text-foot';
    return '';
  },

  colorWinloss(value: number) {
    if (value > 0) return 'text-primary';
    if (value < 0) return 'text-red-500';
    return 'blue-text';
  },

  winloseStatus(value) {
    if (value > 0) return 'Win';
    if (value < 0) return 'Loss';
    return 'Tie';
  },

  getRefreshToken() {
    return sessionGet<string>(SESSION_KEYS.refresh);
  },

  encryptAES(subPassword: string) {
    return utils.encrypt(subPassword);
  },

  encryptAESHeader() {
    const text = 'b34f63f8733328b5c6c9a98354f70137';
    return utils.encrypt(text);
  },

  encryptSensitiveData(request: Record<string, any>) {
    let cloned = { ...request };
    Object.keys(cloned).forEach(dataKey => {
      if (SENSITIVE_KEY.includes(dataKey as any)) {
        cloned = Object.assign(cloned, {
          [dataKey]: utils.encrypt(cloned[dataKey])
        });
      }
    });
    return cloned;
  },

  encrypt(data: any) {
    return key.encrypt(data, 'base64');
  },

  /**
   * Optional installer for Vue app.
   * This allows using dateTimeFormatter/commaFormatter/upperCamel via globalProperties.
   */
  install(app: import('vue').App) {
    app.config.globalProperties.dateTimeFormatter = dateTimeFormatter;
    app.config.globalProperties.commaFormatter = commaFormatter;
    app.config.globalProperties.upperCamel = upperCamel;
  },

  // Expose session helpers so other parts (e.g. axios-interceptors) can use them
  _session: {
    get: sessionGet,
    set: sessionSet,
    remove: sessionRemove
  }
};

export default utils;
